import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserDonations, DonationRecord } from '../../services/donationService';
import { getCurrentUser, getCurrentUserData, logoutUser } from '../../services/authService';
import { Timestamp } from 'firebase/firestore';
import DashboardDonationModal from '../../components/DashboardDonationModal/DashboardDonationModal';
import './Dashboard.css';

type ActiveView = 'dashboard' | 'history' | 'profile';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<ActiveView>('dashboard');
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        const user = getCurrentUser();
        if (!user) {
          setError('Please login to view your dashboard');
          setLoading(false);
          return;
        }

        // Validate user ID from URL parameter
        const urlUserId = searchParams.get('id');
        if (!urlUserId || urlUserId !== user.uid) {
          // Redirect to correct dashboard URL with user's ID
          navigate(`/dashboard?id=${user.uid}`, { replace: true });
          return;
        }

        const currentUserData = await getCurrentUserData();
        setUserData(currentUserData);

        const donationsResult = await getUserDonations();

        if (donationsResult.success && donationsResult.donations) {
          setDonations(donationsResult.donations);
        }
      } catch (err: any) {
        console.error('Error loading dashboard data:', err);
        setError(err.message || 'An error occurred while loading dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [navigate, searchParams]);

  const refreshDonations = async () => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      const donationsResult = await getUserDonations();
      if (donationsResult.success && donationsResult.donations) {
        setDonations(donationsResult.donations);
      }
    } catch (error) {
      console.error('Error refreshing donations:', error);
    }
  };

  const formatDate = (date: Timestamp | Date | string | undefined): string => {
    if (!date) return 'N/A';

    let dateObj: Date;
    if (date instanceof Timestamp) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      return 'N/A';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAmount = (amount: number): string => {
    return `$${amount.toFixed(2)}`;
  };

  const getTimeSinceLastDonation = (): string => {
    if (donations.length === 0) return 'No donations yet';

    const lastDonation = donations[0];
    if (!lastDonation.createdAt) return 'N/A';

    let lastDate: Date;
    if (lastDonation.createdAt instanceof Timestamp) {
      lastDate = lastDonation.createdAt.toDate();
    } else if (lastDonation.createdAt instanceof Date) {
      lastDate = lastDonation.createdAt;
    } else if (typeof lastDonation.createdAt === 'string') {
      lastDate = new Date(lastDonation.createdAt);
    } else {
      return 'N/A';
    }

    const now = new Date();
    const diffMs = now.getTime() - lastDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins} mins ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getDonorDuration = (): string => {
    if (donations.length === 0) return '0 months';

    const firstDonation = donations[donations.length - 1];
    if (!firstDonation.createdAt) return '0 months';

    let firstDate: Date;
    if (firstDonation.createdAt instanceof Timestamp) {
      firstDate = firstDonation.createdAt.toDate();
    } else if (firstDonation.createdAt instanceof Date) {
      firstDate = firstDonation.createdAt;
    } else if (typeof firstDonation.createdAt === 'string') {
      firstDate = new Date(firstDonation.createdAt);
    } else {
      return '0 months';
    }

    const now = new Date();
    const diffMs = now.getTime() - firstDate.getTime();
    const diffMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));

    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  // Calculate stats
  const totalDonations = donations.length;
  const lifetimeDonations = donations.reduce((sum, d) => sum + d.amount, 0);
  const averageDonation = totalDonations > 0 ? lifetimeDonations / totalDonations : 0;

  if (loading) {
    return (
      <div className="dashboard-new-page">
        <div className="dashboard-new-container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-new-page">
        <div className="dashboard-new-container">
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => navigate('/donate-anonymously')}>
              Make a Donation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-new-page">
      <div className="dashboard-new-container">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          {/* User Profile Section */}
          <div className="sidebar-profile">
            <div className="profile-avatar">
              {userData?.firstName?.[0]?.toUpperCase() || 'S'}
            </div>
            <h2 className="profile-name">{userData?.firstName || 'Sophia'} {userData?.lastName || 'Browe'}</h2>
            <div className="profile-info">
              <div className="profile-info-item">
                <span className="info-icon">🕐</span>
                <span className="info-text">Last donated {getTimeSinceLastDonation()}</span>
              </div>
              <div className="profile-info-item">
                <span className="info-icon">💝</span>
                <span className="info-text">Donor for {getDonorDuration()}</span>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveView('dashboard')}
            >
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Dashboard</span>
            </button>
            <button
              className={`nav-item ${activeView === 'history' ? 'active' : ''}`}
              onClick={() => setActiveView('history')}
            >
              <span className="nav-icon">📋</span>
              <span className="nav-text">Donation History</span>
            </button>
            <button
              className={`nav-item ${activeView === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveView('profile')}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">Edit Profile</span>
            </button>
          </nav>

          {/* Logout Button */}
          <button className="sidebar-logout" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Logout</span>
          </button>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main-content">
          {activeView === 'dashboard' && (
            <>
              {/* Stats Section */}
              <section className="stats-section">
                <h2 className="section-title">
                  <span className="title-icon">📊</span>
                  Your Giving Stats
                </h2>
                <div className="stats-cards">
                  <div className="stat-card-new">
                    <div className="stat-icon-new">4</div>
                    <div className="stat-label-new">NUMBER OF<br />DONATIONS</div>
                  </div>
                  <div className="stat-card-new highlight">
                    <div className="stat-value-new">${lifetimeDonations.toFixed(0)}</div>
                    <div className="stat-label-new">LIFETIME<br />DONATIONS</div>
                  </div>
                  <div className="stat-card-new">
                    <div className="stat-value-new">${averageDonation.toFixed(0)}</div>
                    <div className="stat-label-new">AVERAGE<br />DONATION</div>
                  </div>
                </div>
              </section>

              {/* Recent Donations Section */}
              <section className="recent-donations-section">
                <h2 className="section-title">
                  <span className="title-icon">📋</span>
                  Recent Donations
                </h2>
                {donations.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📭</div>
                    <p>You haven't made any donations yet.</p>
                    <button className="btn-primary" onClick={() => setIsDonateModalOpen(true)}>
                      Make Your First Donation
                    </button>
                  </div>
                ) : (
                  <div className="donations-table-wrapper">
                    <table className="donations-table">
                      <thead>
                        <tr>
                          <th>DONATION</th>
                          <th>FORM</th>
                          <th>DATE</th>
                          <th>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.slice(0, 4).map((donation, index) => (
                          <React.Fragment key={donation.id}>
                            <tr className="donation-row">
                              <td className="donation-amount-cell">
                                <div className="amount-value">{formatAmount(donation.amount)}</div>
                                <div className="donation-id">ID: {donation.id.substring(0, 8)}</div>
                              </td>
                              <td>Donation Form</td>
                              <td>{formatDate(donation.createdAt)}</td>
                              <td>
                                <div className="status-cell">
                                  <span className={`status-indicator ${donation.paymentStatus === 'success' ? 'success' : ''}`}>
                                    ● {donation.paymentStatus === 'success' ? 'Complete' : donation.paymentStatus}
                                  </span>
                                  {index === 0 && donations.length === 1 && (
                                    <span className="badge-first-donation">First Donation</span>
                                  )}
                                  <a href="#" className="view-receipt" onClick={(e) => {
                                    e.preventDefault();
                                    alert('Receipt download feature coming soon!');
                                  }}>
                                    View Receipt →
                                  </a>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}

          {activeView === 'history' && (
            <section className="donations-history-section">
              <h2 className="section-title">
                <span className="title-icon">📋</span>
                {totalDonations} Total Donations
              </h2>
              {donations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <p>You haven't made any donations yet.</p>
                </div>
              ) : (
                <div className="donations-table-wrapper">
                  <table className="donations-table">
                    <thead>
                      <tr>
                        <th>DONATION</th>
                        <th>FORM</th>
                        <th>DATE</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((donation, index) => (
                        <React.Fragment key={donation.id}>
                          <tr className="donation-row">
                            <td className="donation-amount-cell">
                              <div className="amount-value">{formatAmount(donation.amount)}</div>
                              <div className="donation-id">ID: {donation.id.substring(0, 8)}</div>
                            </td>
                            <td>Donation Form</td>
                            <td>{formatDate(donation.createdAt)}</td>
                            <td>
                              <div className="status-cell">
                                <span className={`status-indicator ${donation.paymentStatus === 'success' ? 'success' : ''}`}>
                                  ● {donation.paymentStatus === 'success' ? 'Complete' : donation.paymentStatus}
                                </span>
                                {index === donations.length - 1 && (
                                  <span className="badge-first-donation">First Donation</span>
                                )}
                                <a href="#" className="view-receipt" onClick={(e) => {
                                  e.preventDefault();
                                  alert('Receipt download feature coming soon!');
                                }}>
                                  View Receipt →
                                </a>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <div className="table-footer">
                    Showing 1 - {donations.length} of {donations.length} Donations
                  </div>
                </div>
              )}
            </section>
          )}

          {activeView === 'profile' && (
            <section className="profile-settings-section">
              <h2 className="section-title">
                <span className="title-icon">⚙️</span>
                Edit Profile
              </h2>
              {userData && (
                <div className="settings-form">
                  <div className="setting-field">
                    <label className="setting-label">Name</label>
                    <div className="setting-value">{userData.firstName} {userData.lastName}</div>
                    <button className="btn-edit-setting">Edit</button>
                  </div>
                  <div className="setting-field">
                    <label className="setting-label">Email</label>
                    <div className="setting-value">{userData.email}</div>
                    <button className="btn-edit-setting">Edit</button>
                  </div>
                  {userData.phone && (
                    <div className="setting-field">
                      <label className="setting-label">Phone</label>
                      <div className="setting-value">{userData.countryCode} {userData.phone}</div>
                      <button className="btn-edit-setting">Edit</button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Donate Modal */}
      <DashboardDonationModal
        isOpen={isDonateModalOpen}
        onClose={() => setIsDonateModalOpen(false)}
        onSuccess={refreshDonations}
      />
    </div>
  );
};

export default Dashboard;
